import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  BarChart3,
  ChevronsLeft,
  ChevronsRight,
  ClipboardCheck,
  Clock,
  CreditCard,
  FileText,
  History,
  Home,
  LogOut,
  MapPin,
  Monitor,
  Palette,
  Receipt,
  Truck,
  User,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useSidebar } from '@/contexts/SidebarContext';
import { useProfile, type ProfileKey } from '@/contexts/ProfileContext';
import { resolveBrand } from '@/brand';
import { RECOLHA_MENU_ENABLED } from '@/lib/feature-flags';
import { ProfileSwitcher } from '@/components/ProfileSwitcher';

// Sidebar com tres estados visuais:
//   1. Expandida fixada      (collapsed=false)            -> 260px, mostra labels + grupos
//   2. Recolhida fixada      (collapsed=true)             -> 72px, somente icones centralizados
//   3. Recolhida + hover     (collapsed=true, hovered)    -> 260px "flutuando" com sombra
// O margin-left do main em App.tsx segue SEMPRE o estado fixado (collapsed),
// nunca o hover, pra evitar que o conteudo da pagina se mova ao passar o mouse.

interface NavItemDef {
  label: string;
  to: string;
  icon: LucideIcon;
  badge?:
    | { kind: 'live'; text: string }
    | { kind: 'count'; value: number };
  permission?: string;
  adminOnly?: boolean;
  // Feature flag de build. `false` remove o item do menu independente de
  // perfil/permissao. Ausente = sempre visivel.
  enabled?: boolean;
}

interface NavGroupDef {
  title: string;
  items: NavItemDef[];
  adminOnly?: boolean;
}

// Items distribuidos por perfil (PLAN-PERFIS-MENU secao 5.3).
// Filtragem em cascata no render: perfil ativo seleciona o array,
// depois permission filtra item, depois grupo vazio some.
//
// Decisoes do CEO (2026-05-04):
//   - "Usuarios do Sistema" so em Admin (decisao 3.2).
//   - "Rede Credenciada" so em Frotista, NAO duplica em Posto (3.4).
//   - 2 pages WIP do Posto (Dashboard, Relatorios) com badge WIP.
const ITEMS_BY_PROFILE: Record<ProfileKey, NavGroupDef[]> = {
  frotista: [
    {
      title: 'Principal',
      items: [
        { label: 'Dashboard', to: '/', icon: Home },
        {
          label: 'Monitoramento',
          to: '/monitoramento',
          icon: ClipboardCheck,
          badge: { kind: 'live', text: 'LIVE' },
          permission: 'transacoes.visualizar',
        },
        {
          label: 'Notas Fiscais',
          to: '/recolha',
          icon: Receipt,
          permission: 'recolha.cliente.visualizar',
          enabled: RECOLHA_MENU_ENABLED,
        },
      ],
    },
    {
      title: 'Cadastros',
      items: [
        { label: 'Veículos', to: '/veiculos', icon: Truck },
        { label: 'Motoristas', to: '/motoristas', icon: User },
        {
          label: 'Cartões Temporários',
          to: '/cartoes',
          icon: CreditCard,
          permission: 'cartoes.visualizar',
        },
      ],
    },
    {
      title: 'Operação',
      items: [
        {
          label: 'Rede Credenciada',
          to: '/rede',
          icon: MapPin,
          badge: { kind: 'live', text: 'MAPA' },
        },
        {
          label: 'Faturas a Pagar',
          to: '/faturas',
          icon: FileText,
        },
        { label: 'Relatórios', to: '/relatorios', icon: BarChart3 },
      ],
    },
  ],
  posto: [
    {
      title: 'Principal',
      items: [
        { label: 'Dashboard', to: '/posto', icon: Home, permission: 'posto.dashboard' },
      ],
    },
    {
      title: 'Operação',
      items: [
        { label: 'Terminal Web', to: '/terminal', icon: Monitor, permission: 'terminal.visualizar' },
        {
          label: 'Recolha de NFe',
          to: '/rede/recolha',
          icon: Receipt,
          permission: 'recolha.rede.listar_pendentes',
          enabled: RECOLHA_MENU_ENABLED,
        },
        {
          label: 'Relatórios',
          to: '/posto/relatorios',
          icon: BarChart3,
          permission: 'posto.relatorios',
        },
      ],
    },
  ],
  admin: [
    {
      title: 'Configurações',
      items: [
        {
          label: 'Grupos RBAC',
          to: '/admin/grupos-rbac',
          icon: Users,
          permission: 'perfis.visualizar',
        },
        {
          label: 'Auditoria global',
          to: '/auditoria',
          icon: History,
          permission: 'auditoria.visualizar',
        },
        // Fluxos automaticos: modulo `fluxos` nao existe no catalogo
        // RBAC ainda. Item permanece sem permission ate S19 abrir o
        // modulo + actions correspondentes (findings-scale.md S19).
        { label: 'Fluxos automáticos', to: '/admin/fluxos', icon: Clock },
        // Design System: ferramenta interna do time, gated por isAdmin
        // (pattern adminOnly ja existente).
        { label: 'Design System', to: '/admin/design-system', icon: Palette, adminOnly: true },
        {
          label: 'Usuários do Sistema',
          to: '/usuarios',
          icon: Users,
          permission: 'usuarios.visualizar',
        },
      ],
    },
  ],
};

export function Sidebar() {
  // Chamado dentro do componente (nao no module scope) - resolveBrand() e
  // barato/cacheado, mas evita depender da ordem de avaliacao dos modulos
  // no bundle de producao (Rollup pode intercalar chunks de forma diferente
  // do dev server).
  const brand = resolveBrand();
  const { user, logout } = useAuth();
  const { config: themeConfig } = useTheme();
  const { collapsed, toggle } = useSidebar();
  const { activeProfile } = useProfile();
  const [hovered, setHovered] = useState(false);

  if (!user) return null;

  // Sidebar visualmente expandida = nao fixada como recolhida OU mouse em cima.
  const isExpanded = !collapsed || hovered;

  // Defensiva: backend deveria sempre retornar permissions como array, mas em
  // race conditions / state inconsistente pode vir undefined. Crash aqui
  // derruba a app inteira, entao normalizamos.
  const userPerms = Array.isArray(user.permissions) ? user.permissions : [];

  // Filtragem em cascata:
  //   1. Perfil ativo seleciona o array de grupos (vazio se sem perfil).
  //   2. Feature flag de build remove o item (nem admin ve).
  //   3. Cada item passa pelo filtro de permission (admin ignora).
  //   4. Grupos vazios apos filtragem desaparecem.
  const groupsForProfile = activeProfile ? ITEMS_BY_PROFILE[activeProfile] : [];
  const visibleGroups = groupsForProfile
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => {
        if (it.enabled === false) return false;
        if (it.adminOnly && !user.isAdmin) return false;
        if (it.permission && !user.isAdmin && !userPerms.includes(it.permission)) return false;
        return true;
      }),
    }))
    .filter((g) => g.items.length > 0);

  // Set de rotas presentes na sidebar do perfil ativo. Usado pra calcular
  // se um item eh PREFIXO de outro (ex: /posto eh prefixo de
  // /posto/relatorios) - nesse caso o NavLink precisa de end=true pra
  // nao destacar ambos como ativos quando estamos numa rota filha.
  const allVisiblePaths = visibleGroups.flatMap((g) => g.items.map((it) => it.to));

  const initials =
    user.nome
      ?.split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';

  return (
    <aside
      id="sidebar"
      aria-label="Navegação principal"
      className={cn(
        'hidden lg:flex flex-col bg-sidebar text-sidebar-text fixed left-0 top-0 h-screen z-40 border-r border-sidebar-border',
        'transition-[width] duration-200 ease-in-out overflow-x-hidden',
        // Sombra forte quando hover-expand sobre o conteudo (sidebar "flutua")
        collapsed && hovered && 'shadow-2xl shadow-black/40',
      )}
      // Width via style inline porque o Tailwind JIT nao detecta arbitrary
      // values com `var(--...)` em condicionais ternarios. Style inline
      // resolve o CSS var no browser sem depender do build do Tailwind.
      style={{
        // Gradient navy 900 -> 800 (espelha o deck oficial JUST). Cai
        // em background liso pra dispositivos sem suporte a gradient.
        background: 'var(--color-sidebar-bg-gradient, var(--color-sidebar-bg))',
        width: isExpanded ? 'var(--sidebar-width)' : 'var(--sidebar-width-collapsed)',
      }}
      onMouseEnter={() => {
        if (collapsed) setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
    >
      {/* Logo + tagline + botao pin */}
      <div
        className={cn(
          'border-b border-sidebar-border/60 transition-all',
          isExpanded ? 'px-6 pt-7 pb-6' : 'px-3 pt-7 pb-6',
        )}
      >
        <div className="flex items-center justify-between gap-2 min-h-[48px]">
          {isExpanded ? (
            themeConfig.logoUrl ? (
              <img
                src={themeConfig.logoUrl}
                alt="Logo"
                className="max-h-12 w-auto object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              // Logo do brand ativo (JUST ou white-label): SVG inline com
              // texto branco + accent de marca. Sem complementos textuais
              // ("pagamentos", "Gestao de Frota") - a marca fala por si.
              <brand.Logo className="h-9 text-white" />
            )
          ) : themeConfig.logoUrl ? (
            // Fix: recolhida ignorava logoUrl e sempre caia no brand
            // default, mesmo com logo customizado via /admin/design-system.
            <img
              src={themeConfig.logoUrl}
              alt="Logo"
              className="max-h-7 w-auto object-contain mx-auto"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            // Recolhida (72px de largura): so o simbolo (iconOnly). Um
            // wordmark inteiro (ex: "valepag") fica ilegivel nesse espaco;
            // logos sem variante separada (ex: JustLogo) ignoram a prop e
            // renderizam normalmente.
            <div className="flex w-full justify-center">
              <brand.Logo className="h-7 text-white" iconOnly />
            </div>
          )}

          {/* Botao de pin: so aparece quando expandida (fixada ou hover).
              Quando esta recolhida sem hover, o botao some pra nao poluir. */}
          {isExpanded && (
            <button
              type="button"
              onClick={toggle}
              aria-label={collapsed ? 'Fixar menu expandido' : 'Recolher menu'}
              title={collapsed ? 'Fixar menu expandido' : 'Recolher menu'}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sidebar-text-mute transition-all hover:bg-white/5 hover:text-white"
              style={{ color: 'var(--color-sidebar-text-mute)' }}
            >
              {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* ProfileSwitcher: logo abaixo do logo (decisao CEO 2026-05-04).
          Renderiza chip estatico se user tem 1 perfil, dropdown se >1.
          Em modo recolhido mostra so icone centralizado. */}
      <div
        className={cn(
          'border-b border-sidebar-border/60 transition-all',
          isExpanded ? 'px-3 pt-3 pb-3' : 'px-2 pt-3 pb-3',
        )}
      >
        <ProfileSwitcher isExpanded={isExpanded} />
      </div>

      {/* Nav */}
      <nav
        className={cn(
          'flex-1 overflow-y-auto overflow-x-hidden py-4 transition-[padding] duration-200',
          isExpanded ? 'px-3' : 'px-2',
        )}
      >
        {visibleGroups.map((group) =>
          group.items.length === 0 ? null : (
            <div key={group.title} className="mt-4 first:mt-0 mb-1">
              {/* Altura fixa em ambos os estados pra os itens nao "pularem"
                  verticalmente quando alterna entre recolhida e expandida. */}
              <div className="h-[26px] flex items-end px-3 pb-1">
                {isExpanded ? (
                  <p
                    className="text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap"
                    style={{ color: 'var(--neutral-400)' }}
                  >
                    {group.title}
                  </p>
                ) : (
                  <div className="w-full border-t border-white/[0.08]" />
                )}
              </div>
              <ul>
                {group.items.map((item) => (
                  <li key={item.to}>
                    <SideNavItem
                      item={item}
                      isExpanded={isExpanded}
                      // end=true quando esta rota eh PREFIXO de outra
                      // rota da sidebar (ex: /posto vs /posto/relatorios).
                      // Sem isso, NavLink considera o pai ativo em rotas
                      // filhas. Detalhes de recurso (ex: /veiculos/:uuid)
                      // continuam destacando o pai porque NAO sao items
                      // separados na sidebar.
                      requireExactMatch={isPrefixOfAnother(item.to, allVisiblePaths)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ),
        )}
      </nav>

      {/* User card */}
      <div className="p-3 border-t border-sidebar-border/60">
        {isExpanded ? (
          <div
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
            style={{ background: 'rgba(0, 0, 0, 0.25)' }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
              style={{
                // Avatar com gradient goiaba (cor de destaque institucional).
                background:
                  'linear-gradient(135deg, var(--brand-goiaba-400), var(--brand-goiaba-600))',
              }}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user.nome}</p>
              <p
                className="text-[11px] truncate"
                style={{ color: 'var(--color-sidebar-text-mute)' }}
              >
                {user.email}
              </p>
            </div>
            <button
              type="button"
              onClick={() => logout()}
              aria-label="Sair da plataforma"
              title="Sair"
              className="text-sidebar-text-mute hover:text-white transition shrink-0"
              style={{ color: 'var(--color-sidebar-text-mute)' }}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        ) : (
          // Recolhida: avatar centralizado pra preservar identidade visual.
          // Logout fica acessivel via hover-expand (a sidebar abre e mostra o card completo).
          <div className="flex justify-center">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white"
              style={{
                // Avatar com gradient goiaba (cor de destaque institucional).
                background:
                  'linear-gradient(135deg, var(--brand-goiaba-400), var(--brand-goiaba-600))',
              }}
              title={user.nome}
              aria-label={user.nome}
            >
              {initials}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

// Verifica se `path` eh prefixo de outra rota em `allPaths`. Ex:
// '/posto' retorna true se a lista contem '/posto/relatorios'. Ignora
// '/' (sempre tratado como exact via requireExactMatch separado) e
// matches do proprio path.
function isPrefixOfAnother(path: string, allPaths: string[]): boolean {
  if (path === '/') return true;
  const prefix = path.endsWith('/') ? path : path + '/';
  return allPaths.some((other) => other !== path && other.startsWith(prefix));
}

function SideNavItem({
  item,
  isExpanded,
  requireExactMatch,
}: {
  item: NavItemDef;
  isExpanded: boolean;
  requireExactMatch: boolean;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={requireExactMatch}
      title={!isExpanded ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center rounded-md text-[13.5px] font-medium transition mb-0.5',
          isExpanded ? 'gap-3 px-3 py-[9px]' : 'justify-center px-0 py-[9px]',
          isActive
            ? 'text-white'
            : 'text-sidebar-text-mute hover:bg-white/[0.04] hover:text-white',
        )
      }
      style={({ isActive }) =>
        isActive
          ? {
              // Item ativo: gradient goiaba transparente + ring goiaba.
              // Tokens semanticos pra que swap de paleta atualize aqui.
              background: 'var(--color-sidebar-active-gradient)',
              boxShadow:
                'inset 0 0 0 1px rgb(var(--brand-goiaba-500-rgb) / 0.22)',
              color: 'var(--color-text-on-dark)',
            }
          : { color: 'var(--color-sidebar-text-mute)' }
      }
    >
      {({ isActive }) => (
        <>
          {isActive && isExpanded ? (
            <span
              className="absolute -left-[12px] top-1/2 -translate-y-1/2 w-[3px] h-[18px] rounded-r"
              style={{ background: 'var(--color-primary)' }}
              aria-hidden="true"
            />
          ) : null}
          <Icon
            className="w-[18px] h-[18px] flex-shrink-0"
            style={isActive ? { color: 'var(--color-primary)' } : undefined}
          />
          {isExpanded && (
            <>
              <span className="flex-1 truncate">{item.label}</span>
              {item.badge?.kind === 'live' ? <LiveBadge>{item.badge.text}</LiveBadge> : null}
              {item.badge?.kind === 'count' ? <CountBadge>{item.badge.value}</CountBadge> : null}
            </>
          )}
        </>
      )}
    </NavLink>
  );
}

function LiveBadge({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
      style={{
        // Badge "LIVE": fundo goiaba transparente + texto goiaba claro
        // pra contraste em fundo navy escuro.
        background: 'var(--color-primary-soft)',
        color: 'var(--brand-goiaba-200)',
      }}
    >
      <span
        className="w-[5px] h-[5px] rounded-full animate-pulse-dot"
        style={{ background: 'var(--color-primary)' }}
        aria-hidden="true"
      />
      {children}
    </span>
  );
}

function CountBadge({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center justify-center text-[10px] font-bold px-1.5 py-px rounded-full min-w-[18px] text-center"
      style={{
        background: 'var(--color-warning-bg)',
        color: 'var(--color-warning)',
      }}
    >
      {children}
    </span>
  );
}
